import { Box, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { OrganizationService } from "services/OrganizationService";
import type { TeamAnalyst } from "services/OrganizationService";
import DrilldownStore, {
    AssignSelectedConversationsTask,
    EvaluateWithAiTask,
} from "./DrilldownStore";

const organizationService = new OrganizationService();

type EvaluateOption = "ai" | "assign";

type Props = {
    drilldownStore: DrilldownStore;
};

/**
 * AUT-10592: what happens to the conversations picked in the drilldown grid. Either the AI
 * scores them, or they become human evaluations in one teammate's queue.
 */
const EvaluateConversationsDialog = observer(({ drilldownStore }: Props) => {
    const [option, setOption] = useState<EvaluateOption>("ai");
    const [analysts, setAnalysts] = useState<TeamAnalyst[]>([]);
    const [analyst, setAnalyst] = useState<TeamAnalyst>();
    const [analystsLoading, setAnalystsLoading] = useState(false);

    const isOpen = drilldownStore.showEvaluateDialog;
    const selectedCount = drilldownStore.selectedDataDgStore.selectedRows.length;
    const canEvaluateWithAi = drilldownStore.canEvaluateSelectionWithAi;
    const orgId = drilldownStore.authStore.orgStore.selectedOrganization?.id;

    const isLoading =
        drilldownStore.getTaskLoading(EvaluateWithAiTask) ||
        drilldownStore.getTaskLoading(AssignSelectedConversationsTask);

    // A selection holding AI evaluations can only go to a person, so the dialog opens on the
    // one option that applies rather than on a disabled radio.
    useEffect(() => {
        if (!canEvaluateWithAi) setOption("assign");
    }, [canEvaluateWithAi]);

    useEffect(() => {
        if (!isOpen || !orgId) return;

        const loadAnalysts = async () => {
            setAnalystsLoading(true);
            try {
                const teamAnalysts = await organizationService.getTeamAnalysts(
                    orgId,
                    drilldownStore.authStore.effectiveUserId,
                );

                setAnalysts(
                    [...teamAnalysts].sort((left, right) =>
                        (left.userName ?? "").localeCompare(
                            right.userName ?? "",
                        ),
                    ),
                );
            } finally {
                setAnalystsLoading(false);
            }
        };

        void loadAnalysts();
    }, [isOpen, orgId, drilldownStore.authStore.effectiveUserId]);

    const onClose = useCallback(() => {
        drilldownStore.setShowEvaluateDialog(false);
    }, [drilldownStore]);

    const onConfirm = async () => {
        if (option === "ai") {
            await drilldownStore.evaluateSelectionWithAi();
            return;
        }

        if (analyst?.id) {
            await drilldownStore.assignSelectionToUser(analyst.id);
        }
    };

    return (
        <AcxConfirmationDialog
            isOpen={isOpen}
            onClose={onClose}
            title="Evaluate conversations"
            subTitle={`${selectedCount} conversation${
                selectedCount === 1 ? "" : "s"
            } selected`}
            maxWidth="sm"
            fullWidth
            confirmButtonText={option === "ai" ? "Evaluate" : "Assign"}
            confirmButtonId="signals-evaluate-conversations-confirm"
            confirmButtonDisabled={option === "assign" && !analyst}
            isLoading={isLoading}
            onConfirm={onConfirm}
            content={
                <RadioGroup
                    value={option}
                    onChange={(event) =>
                        setOption(event.target.value as EvaluateOption)
                    }
                    id="signals-evaluate-conversations-options"
                >
                    <Box
                        sx={(theme) => ({
                            border: `1px solid ${
                                option === "ai"
                                    ? theme.palette.primary.main
                                    : theme.palette.gray[200]
                            }`,
                            borderRadius: theme.spacing(0.5),
                            padding: theme.spacing(1.5),
                        })}
                    >
                        <FormControlLabel
                            value="ai"
                            control={<Radio />}
                            disabled={!canEvaluateWithAi}
                            id="signals-evaluate-conversations-ai-radio"
                            label={
                                <>
                                    <Typography variant="subtitle2">
                                        Evaluate with AI
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={(theme) => ({
                                            color: theme.palette.gray[600],
                                        })}
                                    >
                                        AI scores the selected conversations
                                        automatically.
                                    </Typography>
                                </>
                            }
                        />
                    </Box>

                    {!canEvaluateWithAi && selectedCount > 0 && (
                        <Typography
                            variant="body2"
                            sx={(theme) => ({
                                color: theme.palette.gray[600],
                                padding: theme.spacing(1, 0),
                            })}
                        >
                            Some selected conversations have already been
                            evaluated by AI. Deselect them to use this option,
                            or assign them to a user instead.
                        </Typography>
                    )}

                    <Box
                        sx={(theme) => ({
                            border: `1px solid ${
                                option === "assign"
                                    ? theme.palette.primary.main
                                    : theme.palette.gray[200]
                            }`,
                            borderRadius: theme.spacing(0.5),
                            marginTop: theme.spacing(1),
                            padding: theme.spacing(1.5),
                        })}
                    >
                        <FormControlLabel
                            value="assign"
                            control={<Radio />}
                            id="signals-evaluate-conversations-assign-radio"
                            label={
                                <>
                                    <Typography variant="subtitle2">
                                        Assign to a user
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={(theme) => ({
                                            color: theme.palette.gray[600],
                                        })}
                                    >
                                        Send the conversations to a teammate for
                                        a human evaluation.
                                    </Typography>
                                </>
                            }
                        />
                    </Box>

                    {option === "assign" && (
                        <Box sx={(theme) => ({ paddingTop: theme.spacing(2) })}>
                            <Typography
                                variant="subtitle2"
                                sx={(theme) => ({
                                    paddingBottom: theme.spacing(0.5),
                                })}
                            >
                                Assign to
                            </Typography>
                            <AcxSelect
                                isMulti={false}
                                id="signals-evaluate-conversations-user-select"
                                options={analysts}
                                onChange={(selected: TeamAnalyst) =>
                                    setAnalyst(selected)
                                }
                                labelField="userName"
                                valueField="id"
                                isClearable
                                placeholder="Select a user"
                                defaultValue={analyst}
                                isLoading={analystsLoading}
                            />
                            <Typography
                                variant="body2"
                                sx={(theme) => ({
                                    color: theme.palette.gray[600],
                                    paddingTop: theme.spacing(0.5),
                                })}
                            >
                                The conversations will appear in their
                                evaluation queue.
                            </Typography>
                        </Box>
                    )}
                </RadioGroup>
            }
        />
    );
});

export default EvaluateConversationsDialog;
